import {
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Image as KonvaImage, Text as KonvaText, Layer, Rect, Stage } from 'react-konva'
import { api } from '../../services/api'
import { getCurrentLabelSize, labelPxToMm, type LabelSize } from '../../utils/labelSize'

const SCREEN_DPI = 96
const MM_PER_INCH = 25.4
const DEFAULT_TEXT_FONT_SIZE = 20
const MIN_TEXT_FONT_SIZE = 8
const MAX_TEXT_FONT_SIZE = 64
const PREVIEW_LINE_HEIGHT = 1.2
const RAW_LABEL_MARGIN_MM = 5
const RAW_LABEL_LOGO_SIZE_MM = 10
const RAW_LABEL_LOGO_TEXT_GAP_MM = 2
const DEFAULT_PREVIEW_ZOOM = 2
const MIN_LOGO_SIZE_MM = 5
const MAX_LOGO_SIZE_MM = 25
const MIN_QUANTITY = 1
const MAX_QUANTITY = 99

type LabelTextBlock = {
  id: string
  text: string
  x: number
  y: number
  fontSize: number
}

function mmToScreenPx(mm: number): number {
  return (mm / MM_PER_INCH) * SCREEN_DPI
}

function mmToLabelPx(mm: number, size: LabelSize): number {
  const widthMm = size.widthCm * 10
  return (mm / widthMm) * size.widthPx
}

function mmToLabelPxY(mm: number, size: LabelSize): number {
  const heightMm = size.heightCm * 10
  return (mm / heightMm) * size.heightPx
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function estimateTextBounds(text: string, fontSize: number): { width: number; height: number } {
  const safe = text || ' '
  const lines = safe.split(/\r?\n/)
  const maxLineLength = lines.reduce((max, line) => Math.max(max, line.length), 0)
  const lineHeightPx = Math.round(fontSize * PREVIEW_LINE_HEIGHT)
  const width = Math.max(fontSize, Math.round(maxLineLength * fontSize * 0.56))
  const height = Math.max(fontSize, lines.length * lineHeightPx)
  return { width, height }
}

export interface ImprimirEtiquetaModalProps {
  open: boolean
  onClose: () => void
  initialText?: string
}

export function ImprimirEtiquetaModal({
  open,
  onClose,
  initialText = '',
}: ImprimirEtiquetaModalProps) {
  const toast = useToast()
  const blockIdRef = useRef(0)
  const [size, setSize] = useState<LabelSize | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null)
  const [showLogo, setShowLogo] = useState(true)
  const [quantity, setQuantity] = useState(1)
  const [quantityInput, setQuantityInput] = useState('1')
  const [logoXmm, setLogoXmm] = useState(RAW_LABEL_MARGIN_MM)
  const [logoYmm, setLogoYmm] = useState(RAW_LABEL_MARGIN_MM)
  const [logoSizeMm, setLogoSizeMm] = useState(RAW_LABEL_LOGO_SIZE_MM)
  const [logoSizeInput, setLogoSizeInput] = useState(String(RAW_LABEL_LOGO_SIZE_MM))
  const [fontSizeInput, setFontSizeInput] = useState('')
  const [previewZoom, setPreviewZoom] = useState(DEFAULT_PREVIEW_ZOOM)
  const [textBlocks, setTextBlocks] = useState<LabelTextBlock[]>([])
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)

  const createBlock = useCallback(
    (params: {
      size: LabelSize
      text?: string
      showLogo: boolean
      yOffsetPx?: number
      logoYmm?: number
      logoSizeMm?: number
    }) => {
      const {
        size: labelSize,
        text = '',
        showLogo: withLogo,
        yOffsetPx = 0,
        logoYmm: logoY = RAW_LABEL_MARGIN_MM,
        logoSizeMm: logoSize = RAW_LABEL_LOGO_SIZE_MM,
      } = params
      const marginPx = mmToLabelPx(RAW_LABEL_MARGIN_MM, labelSize)
      const textAreaTopPx = withLogo
        ? mmToLabelPxY(logoY + logoSize + RAW_LABEL_LOGO_TEXT_GAP_MM, labelSize)
        : marginPx

      blockIdRef.current += 1
      return {
        id: `txt-${Date.now()}-${blockIdRef.current}`,
        text,
        x: marginPx,
        y: textAreaTopPx + yOffsetPx,
        fontSize: DEFAULT_TEXT_FONT_SIZE,
      }
    },
    []
  )

  useEffect(() => {
    if (!open) return

    const nextSize = getCurrentLabelSize()
    setSize(nextSize)
    setShowLogo(true)
    setQuantity(1)
    setQuantityInput('1')
    setLogoXmm(RAW_LABEL_MARGIN_MM)
    setLogoYmm(RAW_LABEL_MARGIN_MM)
    setLogoSizeMm(RAW_LABEL_LOGO_SIZE_MM)
    setLogoSizeInput(String(RAW_LABEL_LOGO_SIZE_MM))
    setFontSizeInput('')
    setPreviewZoom(DEFAULT_PREVIEW_ZOOM)

    const firstBlock = createBlock({
      size: nextSize,
      text: initialText || '',
      showLogo: true,
      logoYmm: RAW_LABEL_MARGIN_MM,
      logoSizeMm: RAW_LABEL_LOGO_SIZE_MM,
    })
    setTextBlocks([firstBlock])
    setSelectedBlockId(firstBlock.id)
  }, [open, initialText, createBlock])

  const selectedBlock = textBlocks.find((block) => block.id === selectedBlockId) || null

  useEffect(() => {
    const block = textBlocks.find((b) => b.id === selectedBlockId)
    if (block) {
      setFontSizeInput(String(block.fontSize))
    } else {
      setFontSizeInput('')
    }
  }, [selectedBlockId])

  useEffect(() => {
    if (!open) return

    let isMounted = true
    const img = document.createElement('img')
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (isMounted) setLogoImage(img)
    }
    img.onerror = () => {
      if (isMounted) {
        setLogoImage(null)
        toast({ description: 'Não foi possível carregar a logo da etiqueta.', status: 'warning' })
      }
    }
    img.src = `${process.env.PUBLIC_URL || ''}/logo_tn_pinturas.png`

    return () => {
      isMounted = false
    }
  }, [open, toast])

  const getBoundedPosition = useCallback(
    (
      block: LabelTextBlock,
      x: number,
      y: number,
      labelSize: LabelSize,
      withLogo: boolean,
      logoXmmVal: number,
      logoYmmVal: number,
      logoSizeMmVal: number
    ) => {
      const marginPx = mmToLabelPx(RAW_LABEL_MARGIN_MM, labelSize)
      const logoXpx = mmToLabelPx(logoXmmVal, labelSize)
      const logoYpx = mmToLabelPxY(logoYmmVal, labelSize)
      const logoWidthPx = mmToLabelPx(logoSizeMmVal, labelSize)
      const logoHeightPx = mmToLabelPxY(logoSizeMmVal, labelSize)
      const logoTextGapPx = mmToLabelPx(RAW_LABEL_LOGO_TEXT_GAP_MM, labelSize)
      const textBounds = estimateTextBounds(block.text, block.fontSize)
      const minTextY = marginPx
      const maxTextY = Math.max(minTextY, labelSize.heightPx - marginPx - textBounds.height)
      const logoTopY = logoYpx
      const logoBottomY = logoYpx + logoHeightPx
      const logoRightX = logoXpx + logoWidthPx

      const resolveMinXForY = (currentY: number) => {
        if (!withLogo) return marginPx
        const textTop = currentY
        const textBottom = currentY + textBounds.height
        const intersectsLogoBand = textTop < logoBottomY && textBottom > logoTopY
        return intersectsLogoBand ? logoRightX + logoTextGapPx : marginPx
      }

      const boundedY = clamp(y, minTextY, maxTextY)
      const minX = resolveMinXForY(boundedY)
      const maxX = Math.max(minX, labelSize.widthPx - marginPx - textBounds.width)
      const boundedX = clamp(x, minX, maxX)

      return { x: boundedX, y: boundedY }
    },
    []
  )

  useEffect(() => {
    if (!size) return
    setTextBlocks((prev) =>
      prev.map((block) => {
        const bounded = getBoundedPosition(block, block.x, block.y, size, showLogo, logoXmm, logoYmm, logoSizeMm)
        if (bounded.x === block.x && bounded.y === block.y) return block
        return { ...block, ...bounded }
      })
    )
  }, [size, showLogo, logoXmm, logoYmm, logoSizeMm, getBoundedPosition])

  const handleSelectedTextChange = (text: string) => {
    if (!selectedBlockId) return
    setTextBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== selectedBlockId) return block
        const nextBlock = { ...block, text }
        if (!size) return nextBlock
        const bounded = getBoundedPosition(nextBlock, nextBlock.x, nextBlock.y, size, showLogo, logoXmm, logoYmm, logoSizeMm)
        return { ...nextBlock, ...bounded }
      })
    )
  }

  const handleSelectedFontSizeChange = (value: string) => {
    if (!selectedBlockId) return
    const parsed = Number(value)
    const nextFontSize = Number.isFinite(parsed)
      ? clamp(parsed, MIN_TEXT_FONT_SIZE, MAX_TEXT_FONT_SIZE)
      : DEFAULT_TEXT_FONT_SIZE

    setTextBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== selectedBlockId) return block
        const nextBlock = { ...block, fontSize: nextFontSize }
        if (!size) return nextBlock
        const bounded = getBoundedPosition(nextBlock, nextBlock.x, nextBlock.y, size, showLogo, logoXmm, logoYmm, logoSizeMm)
        return { ...nextBlock, ...bounded }
      })
    )
  }

  const handleAddText = () => {
    if (!size) return
    const newBlock = createBlock({
      size,
      text: '',
      showLogo,
      yOffsetPx: textBlocks.length * (DEFAULT_TEXT_FONT_SIZE + 8),
      logoYmm,
      logoSizeMm,
    })
    const bounded = getBoundedPosition(newBlock, newBlock.x, newBlock.y, size, showLogo, logoXmm, logoYmm, logoSizeMm)
    const finalBlock = { ...newBlock, ...bounded }
    setTextBlocks((prev) => [...prev, finalBlock])
    setSelectedBlockId(finalBlock.id)
  }

  const handleRemoveSelectedText = () => {
    if (!selectedBlockId) return
    setTextBlocks((prev) => {
      const remaining = prev.filter((block) => block.id !== selectedBlockId)
      setSelectedBlockId(remaining.length > 0 ? remaining[remaining.length - 1].id : null)
      return remaining
    })
  }

  const handleDragEnd = useCallback(
    (id: string, e: { target: { x: () => number; y: () => number } }) => {
      if (!size) return
      const draggedX = e.target.x()
      const draggedY = e.target.y()
      setTextBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== id) return block
          const bounded = getBoundedPosition(block, draggedX, draggedY, size, showLogo, logoXmm, logoYmm, logoSizeMm)
          return { ...block, ...bounded }
        })
      )
    },
    [size, showLogo, logoXmm, logoYmm, logoSizeMm, getBoundedPosition]
  )

  const handleClearPreview = useCallback(() => {
    if (!size) return
    setShowLogo(false)
    setLogoXmm(RAW_LABEL_MARGIN_MM)
    setLogoYmm(RAW_LABEL_MARGIN_MM)
    setLogoSizeMm(RAW_LABEL_LOGO_SIZE_MM)
    const firstBlock = createBlock({ size, text: '', showLogo: false })
    setTextBlocks([firstBlock])
    setSelectedBlockId(firstBlock.id)
  }, [size, createBlock])

  const handleLogoDragEnd = useCallback(
    (e: { target: { x: () => number; y: () => number } }) => {
      if (!size) return
      const widthMm = size.widthCm * 10
      const heightMm = size.heightCm * 10
      const { x_mm, y_mm } = labelPxToMm(e.target.x(), e.target.y(), size)
      const clampedX = clamp(x_mm, 0, Math.max(0, widthMm - logoSizeMm))
      const clampedY = clamp(y_mm, 0, Math.max(0, heightMm - logoSizeMm))
      setLogoXmm(clampedX)
      setLogoYmm(clampedY)
    },
    [size, logoSizeMm]
  )

  const handlePrint = async () => {
    if (!size) return
    try {
      setIsPrinting(true)

      const width_mm = size.widthCm * 10
      const height_mm = size.heightCm * 10
      const fontForBlock = (block: LabelTextBlock) => {
        if (block.id === selectedBlockId && fontSizeInput !== '') {
          const parsed = Number(fontSizeInput)
          if (Number.isFinite(parsed)) return clamp(parsed, MIN_TEXT_FONT_SIZE, MAX_TEXT_FONT_SIZE)
        }
        return block.fontSize
      }
      const text_blocks = textBlocks
        .filter((block) => block.text.trim())
        .map((block) => {
          const { x_mm, y_mm } = labelPxToMm(block.x, block.y, size)
          return { text: block.text.trim(), x_mm, y_mm, font_size: fontForBlock(block) }
        })

      const quantityToSend = clamp(Number(quantityInput) || 1, MIN_QUANTITY, MAX_QUANTITY)
      const body: Record<string, unknown> = {
        width_mm,
        height_mm,
        text_blocks,
        include_logo: showLogo,
        quantity: quantityToSend,
      }
      if (showLogo) {
        body.logo_x_mm = logoXmm
        body.logo_y_mm = logoYmm
        body.logo_size_mm = clamp(
          Number(logoSizeInput) || MIN_LOGO_SIZE_MM,
          MIN_LOGO_SIZE_MM,
          MAX_LOGO_SIZE_MM
        )
      }

      const response = await api.post('/raw-materials/generate-label', body)

      if (response.status === 200) {
        toast({ description: response.data.message, status: 'success' })
        onClose()
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || 'Erro ao gerar a etiqueta'
      toast({ description: msg, status: 'error' })
    } finally {
      setIsPrinting(false)
    }
  }

  if (!size) return null

  const widthMm = size.widthCm * 10
  const heightMm = size.heightCm * 10
  const stageW = mmToScreenPx(widthMm) * previewZoom
  const stageH = mmToScreenPx(heightMm) * previewZoom
  const scale = stageW / size.widthPx

  const marginPx = mmToLabelPx(RAW_LABEL_MARGIN_MM, size)
  const logoXpx = mmToLabelPx(logoXmm, size)
  const logoYpx = mmToLabelPxY(logoYmm, size)
  const logoWidthPx = mmToLabelPx(logoSizeMm, size)
  const logoHeightPx = mmToLabelPxY(logoSizeMm, size)

  const hasPrintableText = textBlocks.some((block) => block.text.trim())

  return (
    <Modal isOpen={open} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" maxW="90vw">
        <ModalHeader>Imprimir Etiqueta</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.400">
              Clique em um texto no preview para editar individualmente e arraste cada bloco separadamente. Arraste a logo para reposicionar.
            </Text>

            <HStack spacing={4} align="center" flexWrap="wrap">
              <HStack spacing={2} align="center">
                <Text fontSize="sm" color="gray.300" minW="80px">
                  Quantidade
                </Text>
                <Input
                  type="number"
                  size="sm"
                  width="70px"
                  min={MIN_QUANTITY}
                  max={MAX_QUANTITY}
                  value={quantityInput}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  onBlur={() => {
                    const n = clamp(Number(quantityInput) || 1, MIN_QUANTITY, MAX_QUANTITY)
                    setQuantity(n)
                    setQuantityInput(String(n))
                  }}
                  bg="gray.700"
                  color="gray.100"
                />
              </HStack>
              {showLogo && (
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="gray.300" minW="140px">
                    Tamanho da logo (mm)
                  </Text>
                  <Input
                    type="number"
                    size="sm"
                    width="70px"
                    min={MIN_LOGO_SIZE_MM}
                    max={MAX_LOGO_SIZE_MM}
                    value={logoSizeInput}
                    onChange={(e) => setLogoSizeInput(e.target.value)}
                    onBlur={() => {
                      const n = clamp(
                        Number(logoSizeInput) || MIN_LOGO_SIZE_MM,
                        MIN_LOGO_SIZE_MM,
                        MAX_LOGO_SIZE_MM
                      )
                      setLogoSizeMm(n)
                      setLogoSizeInput(String(n))
                    }}
                    bg="gray.700"
                    color="gray.100"
                  />
                </HStack>
              )}
            </HStack>

            <Textarea
              placeholder="Selecione um texto no preview para editar"
              value={selectedBlock?.text || ''}
              onChange={(e) => handleSelectedTextChange(e.target.value)}
              size="sm"
              bg="gray.700"
              color="gray.100"
              rows={3}
              isDisabled={!selectedBlock}
            />
            <HStack spacing={2} align="center">
              <Text fontSize="sm" color="gray.300" minW="150px">
                Tamanho da fonte (px)
              </Text>
              <Input
                type="number"
                size="sm"
                width="120px"
                min={MIN_TEXT_FONT_SIZE}
                max={MAX_TEXT_FONT_SIZE}
                value={fontSizeInput}
                onChange={(e) => setFontSizeInput(e.target.value)}
                onBlur={() => {
                  if (!selectedBlockId) return
                  const parsed = Number(fontSizeInput)
                  const n = Number.isFinite(parsed)
                    ? clamp(parsed, MIN_TEXT_FONT_SIZE, MAX_TEXT_FONT_SIZE)
                    : DEFAULT_TEXT_FONT_SIZE
                  handleSelectedFontSizeChange(String(n))
                  setFontSizeInput(String(n))
                }}
                isDisabled={!selectedBlock}
                bg="gray.700"
                color="gray.100"
              />
            </HStack>

            <HStack spacing={3}>
              <Button size="sm" variant="outline" onClick={handleAddText}>
                Adicionar texto
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemoveSelectedText}
                isDisabled={!selectedBlock}
              >
                Remover texto selecionado
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowLogo((prev) => !prev)}>
                {showLogo ? 'Retirar logo' : 'Adicionar logo'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearPreview}>
                Limpar preview
              </Button>
              <Button size="sm" variant={previewZoom === 1 ? 'solid' : 'outline'} onClick={() => setPreviewZoom(1)}>
                100%
              </Button>
              <Button size="sm" variant={previewZoom === 1.5 ? 'solid' : 'outline'} onClick={() => setPreviewZoom(1.5)}>
                150%
              </Button>
              <Button size="sm" variant={previewZoom === 2 ? 'solid' : 'outline'} onClick={() => setPreviewZoom(2)}>
                200%
              </Button>
            </HStack>

            <VStack align="stretch" spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Preview (1:1 com rolagem quando necessário)
              </Text>
              <Box
                border="1px solid"
                borderColor="gray.600"
                borderRadius="md"
                overflow="auto"
                bg="gray.900"
                p={2}
                maxH="60vh"
              >
                <Box width={`${stageW}px`} height={`${stageH}px`} bg="white">
                  <Stage width={stageW} height={stageH}>
                    <Layer scaleX={scale} scaleY={scale}>
                      <Rect x={0} y={0} width={size.widthPx} height={size.heightPx} stroke="#333" strokeWidth={1} />
                      <Rect
                        x={marginPx}
                        y={marginPx}
                        width={Math.max(0, size.widthPx - 2 * marginPx)}
                        height={Math.max(0, size.heightPx - 2 * marginPx)}
                        stroke="#A0AEC0"
                        strokeWidth={1}
                        dash={[5, 4]}
                      />
                      {showLogo && logoImage && (
                        <KonvaImage
                          image={logoImage}
                          x={logoXpx}
                          y={logoYpx}
                          width={logoWidthPx}
                          height={logoHeightPx}
                          draggable
                          onDragEnd={handleLogoDragEnd}
                          dragBoundFunc={(pos: { x: number; y: number }) => ({
                            x: clamp(pos.x, 0, Math.max(0, size.widthPx - logoWidthPx)),
                            y: clamp(pos.y, 0, Math.max(0, size.heightPx - logoHeightPx)),
                          })}
                        />
                      )}

                      {textBlocks.map((block) => (
                        <KonvaText
                          key={block.id}
                          x={block.x}
                          y={block.y}
                          text={block.text || ' '}
                          fontSize={block.fontSize}
                          lineHeight={PREVIEW_LINE_HEIGHT}
                          fontFamily="Arial"
                          fill="black"
                          draggable
                          stroke={selectedBlockId === block.id ? '#3182CE' : undefined}
                          strokeWidth={selectedBlockId === block.id ? 0.5 : 0}
                          onClick={() => setSelectedBlockId(block.id)}
                          onTap={() => setSelectedBlockId(block.id)}
                          onDragEnd={(e: any) => handleDragEnd(block.id, e)}
                          dragBoundFunc={(pos: { x: number; y: number }) => {
                            const bounded = getBoundedPosition(block, pos.x, pos.y, size, showLogo, logoXmm, logoYmm, logoSizeMm)
                            return { x: bounded.x, y: bounded.y }
                          }}
                        />
                      ))}
                    </Layer>
                  </Stage>
                </Box>
              </Box>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="pink"
            onClick={handlePrint}
            isLoading={isPrinting}
            isDisabled={!hasPrintableText && !showLogo}
          >
            Imprimir
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
